import { useState } from 'react'
import { useRouter } from 'next/navigation'
import { Media } from '@/payload-types'
import { useDownloadGate } from '@/lib/downloads/useDownloadGate'
import { useMemberFileHref } from '@/lib/downloads/useFilePass'

type UseResourceDownloadProps = {
  file: Media
  downloadUrl: string
  isGated?: boolean
  isLocked: boolean
  signUpRedirectUrl?: string
}

/**
 * Opens the file in a new tab. Platform rule (Veronika, 2026-09-17): every download link
 * opens the file visibly; a `download` attribute saved large files silently and the
 * button looked dead.
 */
const openFile = (url: string) => {
  const link = document.createElement('a')
  link.href = url
  link.target = '_blank'
  link.rel = 'noopener noreferrer'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

/**
 * Resource downloads are members-only platform-wide (vendor leads — Veronika,
 * 2026-08-26). The legacy isGated/isLocked props no longer weaken the gate: a
 * logged-out visitor is always sent to /login?next=<here> (the Clerk-era
 * /sign-up redirect target has been a 404 since the passwordless login shipped).
 * Members download directly; each download fires the lead-notification email.
 */
export const useResourceDownload = ({
  file,
  downloadUrl,
  signUpRedirectUrl,
}: UseResourceDownloadProps) => {
  const { user, isLoaded, loginHref, notifyDownload } = useDownloadGate()
  // carries the member's file pass (see lib/downloads/filePass.ts)
  const memberHref = useMemberFileHref(downloadUrl)
  const router = useRouter()
  const [isLoading, setIsLoading] = useState(false)

  const handleDownload = () => {
    // session not known yet (page still hydrating): hold the tap, never bounce a member
    if (!isLoaded) return
    if (!user) {
      router.push(
        signUpRedirectUrl ? `/login?next=${encodeURIComponent(signUpRedirectUrl)}` : loginHref,
      )
      return
    }

    setIsLoading(true)
    try {
      notifyDownload({ pdfFilename: file.filename || 'download', pdfUrl: downloadUrl })
      openFile(memberHref)
    } finally {
      setIsLoading(false)
    }
  }

  return {
    handleDownload,
    isLoading,
  }
}
